import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import withTranslate from 'containers/widgets/withTranslate';
import Button from 'components/atoms/Button';
import Icon, { IconSizes, IconVariants } from 'components/foundations/Icon';
import {
    REFERENCE_LINK_DESCRIPTION_MAX_LENGTH,
    REFERENCE_LINK_URL_MAX_LENGTH,
    REFERENCE_LINKS_MAX_COUNT,
} from '../constants';
import {
    ERROR_KEYS,
    isAtMaxLinks,
    parseLinks,
    validateRows,
} from '../utils/referenceLinks';
import css from './ReferenceLinksField.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.AI_CONTEXT.REFERENCE_LINKS';

const emptyRow = () => ({ url: '', description: '' });

const ReferenceLinksField = ({ question, value, onChange = () => {}, readOnly = false, translate }) => {
    const rows = useMemo(() => parseLinks(value), [value]);
    const rowErrors = useMemo(() => validateRows(rows), [rows]);

    // Preserve blank rows in the parent value so the user can keep editing them.
    // Filtering happens server-side and in export/answered helpers.
    const emit = nextRows => {
        onChange(question.id, nextRows.length === 0 ? '' : JSON.stringify(nextRows));
    };

    const handleAdd = () => {
        if (readOnly || isAtMaxLinks(rows)) return;
        emit([...rows, emptyRow()]);
    };

    const handleRemove = index => {
        if (readOnly) return;
        emit(rows.filter((_, i) => i !== index));
    };

    const handleFieldChange = (index, field, fieldValue) => {
        if (readOnly) return;
        const next = rows.map((row, i) => (i === index ? { ...row, [field]: fieldValue } : row));
        emit(next);
    };

    const renderReadOnlyRows = () => (
        <div className={css.readOnlyList}>
            {rows.length === 0 && <div className={css.empty}>{translate(`${TRANSLATION_PREFIX}.EMPTY`)}</div>}
            {rows.map((row, index) => (
                <div key={index} className={css.readOnlyRow}>
                    <a className={css.readOnlyUrl} href={row.url} target="_blank" rel="noopener noreferrer">
                        {row.url}
                    </a>
                    {row.description && <div className={css.readOnlyDescription}>{row.description}</div>}
                </div>
            ))}
        </div>
    );

    if (readOnly) {
        return (
            <div className={css.field}>
                <div className={css.header}>
                    <div className={css.title}>{translate(question.title)}</div>
                    <div className={css.description}>{translate(question.description)}</div>
                </div>
                {renderReadOnlyRows()}
            </div>
        );
    }

    const atMax = isAtMaxLinks(rows);

    return (
        <div className={css.field}>
            <div className={css.header}>
                <div className={css.title}>{translate(question.title)}</div>
                <div className={css.description}>{translate(question.description)}</div>
            </div>

            <div className={css.rows}>
                {rows.map((row, index) => {
                    const errors = rowErrors[index] || {};
                    const isRowPopulated = (row.url || '').length > 0 || (row.description || '').length > 0;
                    const showUrlError = !!errors.url && isRowPopulated;
                    return (
                        <div key={index} className={css.row}>
                            <div className={css.inputs}>
                                <div className={classNames(css.inputGroup, css.urlGroup)}>
                                    <input
                                        className={classNames(css.input, {
                                            [css.inputError]: showUrlError,
                                        })}
                                        type="text"
                                        value={row.url}
                                        placeholder={translate(`${TRANSLATION_PREFIX}.URL_PLACEHOLDER`)}
                                        maxLength={REFERENCE_LINK_URL_MAX_LENGTH}
                                        onChange={e => handleFieldChange(index, 'url', e.target.value)}
                                    />
                                    {showUrlError && (
                                        <div className={css.error}>{translate(errors.url)}</div>
                                    )}
                                </div>
                                <div className={classNames(css.inputGroup, css.descriptionGroup)}>
                                    <input
                                        className={classNames(css.input, {
                                            [css.inputError]: !!errors.description,
                                        })}
                                        type="text"
                                        value={row.description}
                                        placeholder={translate(`${TRANSLATION_PREFIX}.DESCRIPTION_PLACEHOLDER`)}
                                        maxLength={REFERENCE_LINK_DESCRIPTION_MAX_LENGTH}
                                        onChange={e => handleFieldChange(index, 'description', e.target.value)}
                                    />
                                    {errors.description && (
                                        <div className={css.error}>{translate(errors.description)}</div>
                                    )}
                                </div>
                            </div>
                            <button
                                type="button"
                                className={css.removeButton}
                                onClick={() => handleRemove(index)}
                                aria-label={translate(`${TRANSLATION_PREFIX}.REMOVE_LINK`)}
                                title={translate(`${TRANSLATION_PREFIX}.REMOVE_LINK`)}
                            >
                                <Icon
                                    name="trash"
                                    size={IconSizes.SMALL}
                                    variant={IconVariants.LIGHT}
                                    interactive={false}
                                />
                            </button>
                        </div>
                    );
                })}
            </div>

            <div className={css.addRow}>
                <Button type="flat" Icon="plus" onClick={handleAdd} disabled={atMax}>
                    {translate(`${TRANSLATION_PREFIX}.ADD_LINK`)}
                </Button>
                <span className={css.counter}>
                    {translate(`${TRANSLATION_PREFIX}.COUNTER`, {
                        count: rows.length,
                        max: REFERENCE_LINKS_MAX_COUNT,
                    })}
                </span>
            </div>
        </div>
    );
};

ReferenceLinksField.propTypes = {
    question: PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
    }).isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func,
    readOnly: PropTypes.bool,
    translate: PropTypes.func.isRequired,
};

export default withTranslate(ReferenceLinksField);
